/**
 * Bedsheet Bundle in the cart: drawer and /cart (snippets/bedsheet-bundle-cart-line.liquid, -nudge.liquid).
 *  - Stepper: sets every line of the bundle to the new quantity in one /cart/update.js; "+" stops at the
 *    theme's "Bundles per cart" (App A discounts no more than that).
 *  - Bin: removes all of the bundle's lines (no confirm dialog, no toast); the cart re-renders without it.
 *  - "Make it a Bundle" nudge: opens the bundle sheet in "complete" mode, loading
 *    assets/bedsheet-bundle.{js,css} first on pages that don't have them (no PDP rail), warmed up before
 *    the tap so the sheet opens at once.
 * Also owns the toast, the section refresh and the write queue both surfaces use (window.BedsheetBundleCart).
 *
 * Correct under repeated taps, slow networks and other tabs, with no extra request on the way:
 *  - One cart write at a time (serial(); assets/bedsheet-bundle.js shares the queue through
 *    window.__bbCartQueue). A tap that lands while a write is in flight waits its turn.
 *  - A write decides from the cart AS RENDERED WHEN IT RUNS, not when it was tapped. Each write's
 *    response re-renders the drawer / page before the next write starts, so that markup is the server's
 *    latest state in this tab. Three quick "+" taps are three +1s from the real quantity, each checked
 *    against the cap; a second bin tap finds the bundle gone and does nothing.
 *  - Another tab can change the cart: when this tab becomes visible again (or is restored from the
 *    back/forward cache) the cart is read once and the markup re-rendered only if it differs
 *    ([data-bb-cart-sig], rendered by the nudge snippet).
 * Loaded once per page (snippets/cart-drawer.liquid; the /cart sections too); re-renders don't re-run
 * it, so every control is handled by delegation on document.
 */
(function () {
  'use strict';

  if (window.BedsheetBundleCart) return;

  var DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  // The /cart page's own sections (same ids/selectors as CartItems.getSectionsToRender in cart-v2.js).
  function pageSections() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    var out = [];
    if (items && items.dataset.id) out.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.main-cart-contents' });
    if (footer && footer.dataset.id) out.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    return out;
  }
  // Section ids to request with a cart write: the drawer's, plus the /cart page's when on /cart.
  function sections() {
    var ids = DRAWER_SECTIONS.slice();
    pageSections().forEach(function (s) { if (ids.indexOf(s.section) === -1) ids.push(s.section); });
    return ids;
  }
  function onCartPage() { return /\/cart\/?$/.test(window.location.pathname) || pageSections().length > 0; }

  // After the next paint: SCAnalytics fans out to GTM synchronously, which must not delay the tap's
  // request or its visible response.
  function track(name, params) {
    var payload = params || {};
    requestAnimationFrame(function () {
      setTimeout(function () {
        try {
          if (window.SCAnalytics && typeof window.SCAnalytics.push === 'function') window.SCAnalytics.push(name, payload);
          else (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload));
        } catch (e) {}
      }, 0);
    });
  }
  function noop() {}
  function parse(json, fallback) { try { return JSON.parse(json); } catch (e) { return fallback; } }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok || j.status) throw new Error(j.description || j.message || ('HTTP ' + r.status));
        return j;
      });
    });
  }
  function getJson(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }
  function postWithSections(url, body) {
    return post(url, Object.assign({ sections: sections(), sections_url: window.location.pathname }, body));
  }

  // ---- Write queue + the rendered cart ----------------------------------------------------------
  // One cart write at a time across the Bundle's scripts: a task starts only after the previous one has
  // finished, re-render included. Tasks never call serial() themselves (that would wait on itself).
  function serial(task) {
    var run = (window.__bbCartQueue || Promise.resolve()).then(function () { return task(); });
    window.__bbCartQueue = run.then(noop, noop);
    return run;
  }
  function drawer() { return document.querySelector('cart-drawer'); }
  // The cart as last rendered in this tab: the /cart page's items when on /cart (the page's own writes
  // re-render only the page), else the drawer.
  function cartRoot() { return document.getElementById('main-cart-items') || drawer(); }
  function bundleLines(root) { return root ? [].slice.call(root.querySelectorAll('[data-bb-line]')) : []; }
  function findLine(root, bundleId) {
    return bundleLines(root).filter(function (l) { return l.getAttribute('data-bundle-id') === bundleId; })[0] || null;
  }
  // Bundles in the cart = the sum of the grouped lines' quantities.
  function bundlesInCart(root) {
    return bundleLines(root).reduce(function (n, l) { return n + (parseInt(l.getAttribute('data-qty'), 10) || 0); }, 0);
  }
  // The same string the nudge snippet renders into [data-bb-cart-sig].
  function cartSig(cart) {
    return (cart.items || []).map(function (i) { return i.key + ':' + i.quantity + ','; }).join('');
  }
  // The sections on their own (Section Rendering API), from this page so its template's sections render.
  // No JSON Accept header: with one, /cart answers as /cart.js (and a product URL as its .js), not the map.
  function fetchSections() {
    return fetch(window.location.pathname + '?sections=' + sections().join(','))
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (s) {
        if (!s || typeof s['cart-drawer'] !== 'string') throw new Error('sections missing');
        return s;
      });
  }

  // Re-render the drawer (and the /cart page when on /cart) from a write's response, then tell the other
  // cart subscribers (PDP rail, bundle card). Resolves once the markup is current, so the next queued
  // write reads the new state. Shopify now and then omits `sections` from a write's response: those are
  // fetched instead. The /cart page swaps its items + footer in place when both exist before and after
  // the write; when the cart empties (the page's markup is all inside `cart != empty`) or comes back
  // from empty, it reloads instead.
  function render(res) {
    var ready = res && res.sections && res.sections['cart-drawer']
      ? Promise.resolve(res)
      : fetchSections().then(function (s) { return Object.assign({}, res || {}, { sections: s }); });
    return ready.then(function (r) { return apply(r); }, function () {
      if (onCartPage()) { window.location.reload(); return new Promise(noop); }
      var cd = drawer();
      return cd && typeof cd.refresh === 'function' ? cd.refresh() : undefined;
    });
  }
  function apply(res) {
    var cd = drawer();
    if (cd && res.sections['cart-drawer'] && typeof cd.renderContents === 'function') cd.renderContents(res);
    if (onCartPage()) {
      var page = pageSections();
      var sectionId = page.length ? page[0].section : null;
      var html = sectionId ? res.sections[sectionId] : null;
      var doc = html ? new DOMParser().parseFromString(html, 'text/html') : null;
      var swaps = page.map(function (s) {
        var host = document.getElementById(s.id);
        var target = host && (host.querySelector(s.selector) || host);
        var fresh = doc && doc.querySelector('#' + s.id + ' ' + s.selector);
        return { target: target, fresh: fresh };
      });
      if (!page.length || swaps.some(function (x) { return !x.target || !x.fresh; })) {
        window.location.reload();
        return new Promise(noop); // the page is going away: hold the queue
      }
      swaps.forEach(function (x) { x.target.innerHTML = x.fresh.innerHTML; });
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bedsheet-bundle', cartData: res });
    }
  }
  function setBusy(line, busy) {
    if (!line || !line.isConnected) return;
    line.classList.toggle('is-busy', busy);
    [].forEach.call(line.querySelectorAll('button'), function (b) { b.disabled = busy; });
  }
  function limitToast(title, max) {
    toast(max + ' ' + (title || 'Bedsheet Bundle') + (max > 1 ? 's' : '') + ' per order');
    track('sheet_bundle_limit', { where: 'cart', max: max });
  }

  // ---- Stepper ---------------------------------------------------------------------------------
  // The tapped line only names the bundle; quantity, keys and the cap are read when the write runs.
  function changeQty(line, delta) {
    var id = line.getAttribute('data-bundle-id');
    setBusy(line, true);
    serial(function () {
      var root = cartRoot();
      var cur = findLine(root, id);
      if (!cur) { setBusy(line, false); return; } // gone already (an earlier tap removed it)
      var qty = (parseInt(cur.getAttribute('data-qty'), 10) || 1) + delta;
      if (qty <= 0) return removeNow(cur);
      if (delta > 0) {
        var max = parseInt(cur.getAttribute('data-max'), 10) || 1;
        if (bundlesInCart(root) >= max) {
          setBusy(line, false); setBusy(cur, false);
          limitToast(cur.getAttribute('data-title'), max);
          return;
        }
      }
      var updates = {};
      parse(cur.getAttribute('data-keys'), []).forEach(function (k) { updates[k] = qty; });
      setBusy(cur, true);
      track('cart_sheet_bundle_qty', { qty: qty });
      return postWithSections('/cart/update.js', { updates: updates })
        .then(function (res) { return render(res); }, function (err) {
          console.error('Bedsheet Bundle: quantity change failed', { message: err && err.message, sent: updates });
          toast("Couldn't update your Bundle. Please try again.");
          return render(null);
        });
    });
  }

  // ---- Remove (no toast, no Undo: the cart re-renders without it) -------------------------------
  function remove(line) {
    var id = line.getAttribute('data-bundle-id');
    setBusy(line, true);
    serial(function () {
      var cur = findLine(cartRoot(), id);
      if (!cur) { setBusy(line, false); return; } // already removed
      return removeNow(cur);
    });
  }
  // Runs inside a queued task.
  function removeNow(cur) {
    var updates = {};
    parse(cur.getAttribute('data-keys'), []).forEach(function (k) { updates[k] = 0; });
    setBusy(cur, true);
    track('cart_sheet_bundle_remove', {});
    return postWithSections('/cart/update.js', { updates: updates })
      .then(function (res) { return render(res); }, function (err) {
        console.error('Bedsheet Bundle: remove failed', { message: err && err.message, sent: updates });
        toast("Couldn't remove your Bundle. Please try again.");
        return render(null);
      });
  }

  // ---- Another tab changed the cart --------------------------------------------------------------
  // Re-read it when this tab is shown again (or restored from the back/forward cache), and re-render only
  // if the markup no longer matches. No markers = the markup is the empty cart. Only on pages that show
  // the bundle (a grouped line or the strip, drawer included): elsewhere there's nothing of ours to correct,
  // so no request. Absences under AWAY_MS are ignored (a tap into an embedded video or chat frame blurs
  // the window; no other tab changed anything in that time).
  var AWAY_MS = 1500;
  function bundleShown() { return !!document.querySelector('[data-bb-line], [data-bb-nudge]'); }
  var hiddenAt = 0, resyncQueued = false;
  function resync() {
    if (resyncQueued) return;
    resyncQueued = true;
    serial(function () {
      resyncQueued = false;
      return getJson('/cart.js').then(function (cart) {
        var marks = [].slice.call(document.querySelectorAll('[data-bb-cart-sig]'));
        var sig = cartSig(cart);
        var current = marks.length
          ? marks.every(function (m) { return m.getAttribute('data-bb-cart-sig') === sig; })
          : cart.item_count === 0;
        if (current) return;
        return fetchSections().then(function (s) { return render({ sections: s, item_count: cart.item_count }); });
      });
    }).catch(noop);
  }
  function cameBack(since) { return since && Date.now() - since >= AWAY_MS && bundleShown(); }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    var since = hiddenAt;
    hiddenAt = 0;
    if (cameBack(since)) resync();
  });
  // Two windows side by side never hide: coming back to this one is a window focus instead.
  var blurredAt = 0;
  window.addEventListener('blur', function () { blurredAt = Date.now(); });
  window.addEventListener('focus', function () {
    var since = blurredAt;
    blurredAt = 0;
    if (cameBack(since)) resync();
  });
  window.addEventListener('pageshow', function (e) { if (e.persisted && bundleShown()) resync(); });

  // ---- Nudge → sheet ("complete" mode) --------------------------------------------------------
  var loading = null;
  function loadBundle(cfg) {
    if (window.BedsheetBundle) return Promise.resolve();
    if (loading) return loading;
    function css() {
      if (!cfg.css || document.getElementById('bedsheet-bundle-css')) return Promise.resolve();
      return new Promise(function (resolve) {
        var l = document.createElement('link');
        l.rel = 'stylesheet';
        l.id = 'bedsheet-bundle-css';
        l.href = cfg.css;
        l.onload = l.onerror = function () { resolve(); };
        document.head.appendChild(l);
      });
    }
    function js() {
      return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = cfg.js;
        s.onload = function () { resolve(); };
        s.onerror = function () { reject(new Error('Bedsheet Bundle script failed to load')); };
        document.head.appendChild(s);
      });
    }
    loading = Promise.all([css(), js()]).catch(function (e) { loading = null; throw e; });
    return loading;
  }
  // The sheet product's variants (availability per colour + size), fetched once.
  var productReq = null;
  function productJs(cfg) {
    if (!productReq) {
      productReq = getJson('/products/' + encodeURIComponent(cfg.handle) + '.js')
        .catch(function (e) { productReq = null; throw e; });
    }
    return productReq;
  }
  // Everything the sheet needs: its script + CSS and the product's variants, fetched in parallel (one
  // round trip, not two), then handed over so opening needs no network at all.
  function prepare(cfg) {
    return Promise.all([loadBundle(cfg), productJs(cfg)]).then(function (r) {
      if (window.BedsheetBundle.prime) window.BedsheetBundle.prime(cfg, r[1]);
    });
  }
  // Warm-up, so a tap opens the sheet at once: as soon as a nudge is on screen (the /cart page once it's
  // parsed, or the drawer opening), on idle; at the latest on the first touch / hover / focus of it.
  var warmed = false, warmTypes = ['pointerdown', 'mouseover', 'focusin'], drawerWatch = null;
  function warm(btn) {
    if (warmed || !btn) return;
    var cfg = parse(btn.getAttribute('data-config'), null);
    if (!cfg) return;
    warmed = true;
    warmTypes.forEach(function (t) { document.removeEventListener(t, onWarmEvent, true); });
    if (drawerWatch) drawerWatch.disconnect();
    prepare(cfg).catch(function () { warmed = false; });
  }
  function visibleNudge() {
    // Anything that opens the sheet: the nudge, or a bundle line's "Edit colours & sizes". The closed
    // drawer's have no box; on /cart the page's are the visible ones.
    return [].filter.call(document.querySelectorAll('[data-bb-nudge], [data-bb-line-edit]'), function (b) { return b.getClientRects().length > 0; })[0] || null;
  }
  function warmIfShown() {
    if (warmed || !visibleNudge()) return;
    var go = function () { warm(visibleNudge()); };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 1500 }); else setTimeout(go, 300);
  }
  function onWarmEvent(e) {
    var host = e.target && e.target.closest && e.target.closest('[data-bb-nudge-card], [data-bb-line]');
    if (host) warm(host.querySelector('[data-bb-nudge], [data-bb-line-edit]'));
  }
  warmTypes.forEach(function (t) { document.addEventListener(t, onWarmEvent, { passive: true, capture: true }); });
  (function watchDrawer() {
    var cd = drawer();
    if (!cd || !('MutationObserver' in window)) return;
    drawerWatch = new MutationObserver(function () { if (cd.classList.contains('active')) warmIfShown(); });
    drawerWatch.observe(cd, { attributes: true, attributeFilter: ['class'] });
  })();
  // This file is deferred, so the page is parsed by now; no need to wait for every third party's load.
  warmIfShown();

  function openComplete(btn) {
    // One open at a time: ignore taps while it loads, or while the sheet is already up.
    if (btn.getAttribute('aria-busy') === 'true') return;
    if (window.BedsheetBundle && window.BedsheetBundle.isOpen && window.BedsheetBundle.isOpen()) return;
    var cfg = parse(btn.getAttribute('data-config'), null);
    if (!cfg) return;
    track('cart_sheet_bundle_nudge_tap', {});
    btn.setAttribute('aria-busy', 'true');
    prepare(cfg)
      .then(function () {
        return window.BedsheetBundle.open({
          mode: 'complete',
          base: parse(btn.getAttribute('data-colours'), []),
          sizes: parse(btn.getAttribute('data-sizes'), []),
          keys: parse(btn.getAttribute('data-keys'), []),
          // What the cart looked like at the tap: the sheet's write checks it's still so.
          sig: btn.getAttribute('data-keys'),
          config: cfg,
          source: 'cart_nudge',
          opener: btn
        });
      })
      .catch(function (e) { console.error('Bedsheet Bundle:', e); })
      .then(function () { btn.removeAttribute('aria-busy'); });
  }

  // "Edit colours & sizes" on a bundle line: the sheet on THAT bundle (its id + lines from the line's
  // markup); "Update bundle" replaces its lines, "Done" when nothing changed.
  function openEdit(btn) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    if (window.BedsheetBundle && window.BedsheetBundle.isOpen && window.BedsheetBundle.isOpen()) return;
    var line = btn.closest('[data-bb-line]');
    var cfg = parse(btn.getAttribute('data-config'), null);
    if (!line || !cfg) return;
    var bundle = { id: line.getAttribute('data-bundle-id'), lines: parse(line.getAttribute('data-items'), []) };
    track('cart_sheet_bundle_edit_tap', {});
    btn.setAttribute('aria-busy', 'true');
    prepare(cfg)
      .then(function () {
        return window.BedsheetBundle.open({ mode: 'edit', bundle: bundle, config: cfg, source: 'cart_line', opener: btn });
      })
      .catch(function (e) { console.error('Bedsheet Bundle:', e); })
      .then(function () { btn.removeAttribute('aria-busy'); });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-bb-line-qty], [data-bb-line-remove], [data-bb-line-edit], [data-bb-nudge]');
    if (!t) return;
    if (t.hasAttribute('data-bb-nudge')) { openComplete(t); return; }
    if (t.hasAttribute('data-bb-line-edit')) { openEdit(t); return; }
    var line = t.closest('[data-bb-line]');
    if (!line || line.classList.contains('is-busy')) return;
    if (t.hasAttribute('data-bb-line-remove')) remove(line);
    else changeQty(line, parseInt(t.getAttribute('data-bb-line-qty'), 10) || 0);
  });

  // ---- Toast -----------------------------------------------------------------------------------
  var toastEl = null, toastTimer = null;
  var ICON_CHECK = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  // `sub`: an optional second line (the Bundle's sizes: "2 Single/Double · 1 Queen/King").
  function toast(text, action, sub) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bb-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
      toastEl.addEventListener('click', function (e) {
        if (e.target.closest('.bb-toast__act') && toastEl._act) toastEl._act();
      });
    }
    toastEl._act = action ? action.fn : null;
    toastEl.classList.toggle('has-sub', !!sub);
    toastEl.innerHTML = '<span class="bb-toast__icon">' + ICON_CHECK + '</span><span class="bb-toast__text"><span>' + esc(text) + '</span>' +
      (sub ? '<span class="bb-toast__sub">' + esc(sub) + '</span>' : '') + '</span>' +
      (action ? '<button type="button" class="bb-toast__act">' + esc(action.label) + '</button>' : '');
    // Centred over the open drawer on wider screens, else over the page.
    var inner = document.querySelector('cart-drawer.active .drawer__inner');
    if (inner && window.innerWidth >= 750) {
      var r = inner.getBoundingClientRect();
      toastEl.style.left = (r.left + r.width / 2) + 'px';
    } else {
      toastEl.style.left = '';
    }
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, action ? 5000 : 2800);
  }
  function hideToast() { if (toastEl) toastEl.classList.remove('is-on'); }

  window.BedsheetBundleCart = {
    toast: toast,
    hideToast: hideToast,
    /** Section ids to request with a cart write (drawer + /cart page when on /cart). */
    sections: sections,
    /** Re-render the drawer / cart page from a write's response and publish cartUpdate. Returns a Promise. */
    render: render,
    /** Run a cart write after any in-flight one (shared queue). */
    serial: serial,
    /** Re-read the cart and re-render if another tab changed it (also runs on tab show / window focus). */
    resync: resync
  };
})();
